/**
 * T1251 · Devtool channel · cache:miss with cause classification.
 *
 * Spec 05 §L2828 + cache-devtool panel 1. Subscribes once to
 * `QueryCache.subscribe('observerAdded')` and emits `cache:miss` with a
 * cause classification when an observer attaches to a query that has
 * no fresh data.
 *
 * **Causes** (mutually exclusive; first match wins):
 *
 *   - `cold`               — no `dataUpdatedAt` (first ever read).
 *   - `stale`              — `Date.now() - dataUpdatedAt > staleTime`.
 *   - `invalidated-by-NATS` — `state.isInvalidated` AND
 *                            `meta.invalidatedBy === 'nats'`.
 *   - `invalidated-by-tag` — `state.isInvalidated` AND
 *                            `meta.invalidatedBy === 'tag'`.
 *   - `manual`             — `state.isInvalidated` without meta marker.
 *   - `SSR-mismatch`       — `meta.ssr === true` (server data shape
 *                            differs from client expectation; see
 *                            broadcast `dropped:dev-assert` cousin).
 *
 * If the query already has data (`dataUpdatedAt` present AND not stale
 * AND not invalidated), NO emit fires — observer attaches against a
 * warm cache row.
 *
 * The emitter is installed via `installCacheMissChannel(qc)` and
 * returned uninstall closure detaches the subscription.
 */

import { emit, isDevtoolEnabled } from '../events.js';

/** Cause classification — pinned union. */
export type CacheMissCause =
  | 'cold'
  | 'stale'
  | 'invalidated-by-NATS'
  | 'invalidated-by-tag'
  | 'manual'
  | 'SSR-mismatch';

/** Public event payload — every miss carries this shape. */
export interface CacheMissEvent {
  readonly queryHash: string;
  readonly queryKey: readonly unknown[];
  readonly cause: CacheMissCause;
  readonly staleTime: number;
  readonly dataUpdatedAt: number;
  readonly isInvalidated: boolean;
}

/**
 * Minimal structural shape of a TanStack `Query` we depend on. Kept
 * structural so this module doesn't take a direct runtime dep on
 * `@tanstack/query-core` at the type level.
 */
export interface QueryLike {
  readonly queryHash: string;
  readonly queryKey: readonly unknown[];
  readonly meta?: {
    readonly invalidatedBy?: 'nats' | 'tag' | string;
    readonly ssr?: boolean;
  };
  readonly options?: { readonly staleTime?: number };
  readonly state: {
    readonly dataUpdatedAt: number;
    readonly isInvalidated: boolean;
  };
}

/** Subscribe event a `QueryCache` emits — we react to `observerAdded`. */
export interface QueryCacheEventLike {
  readonly type: string;
  readonly query: QueryLike;
}

/** Structural `QueryCache` surface — `subscribe(cb) → unsubscribe`. */
export interface QueryCacheLike {
  subscribe(cb: (e: QueryCacheEventLike) => void): () => void;
}

/** Structural `QueryClient` surface for the install entry point. */
export interface QueryClientLike {
  getQueryCache(): QueryCacheLike;
}

/**
 * Classify the miss cause. Returns `null` when the observer attaches
 * to a warm row (no emit should fire).
 */
export function classifyCacheMissCause(query: QueryLike): CacheMissCause | null {
  const { dataUpdatedAt, isInvalidated } = query.state;
  const staleTime = query.options?.staleTime ?? 0;
  const meta = query.meta;

  // Cold — never had data.
  if (dataUpdatedAt === 0) return 'cold';

  if (isInvalidated) {
    const tag = meta?.invalidatedBy;
    if (tag === 'nats') return 'invalidated-by-NATS';
    if (tag === 'tag') return 'invalidated-by-tag';
    return 'manual';
  }

  // SSR-mismatch is reported via meta.ssr — it overrides "warm".
  if (meta?.ssr === true) return 'SSR-mismatch';

  // Stale window check.
  if (Date.now() - dataUpdatedAt > staleTime) return 'stale';

  // Warm row — no miss.
  return null;
}

/**
 * Install the cache:miss channel against a `QueryClient`. Idempotent
 * uninstall via the returned closure.
 */
export function installCacheMissChannel(qc: QueryClientLike): () => void {
  if (!isDevtoolEnabled()) {
    return (): void => {};
  }
  const cache = qc.getQueryCache();
  return cache.subscribe((event: QueryCacheEventLike) => {
    if (event.type !== 'observerAdded') return;
    const cause = classifyCacheMissCause(event.query);
    if (cause === null) return;
    const payload: CacheMissEvent = {
      queryHash: event.query.queryHash,
      queryKey: event.query.queryKey,
      cause,
      staleTime: event.query.options?.staleTime ?? 0,
      dataUpdatedAt: event.query.state.dataUpdatedAt,
      isInvalidated: event.query.state.isInvalidated,
    };
    emit('cache:miss', payload);
  });
}
